// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { StyledOverlayComponent } from "@vanmeter/App/Components/Common/StyledOverlay";
import { menuBodyStyle } from "@vanmeter/App/Components/Menu/DropdownMenu";
import { autoClamp, fontConfig } from "@vanmeter/XDL/constants";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color, getSystemColor, styledScrollbar, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export const zIndexLoadingOverlay = 1500;

export const hideLoadingOverlay = css`
    #react-app:not(.allow-loadingOverlay) {
        svg[class^="LoadingSpinnerStyle"],
        div[class^="LoadingOverlayStyle"] {
            visibility: hidden;
        }
    }
`;

export const xngLoadingOverlayUtilitiesStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

export const specialClassesStyles = css`
    // add general color classes
    .as-blue {
        color: ${color("primary")};
    }
    .as-yellow {
        color: ${color("secondary")};
    }
    .as-orange {
        color: ${color("common.ui8")};
    }
    .as-white {
        color: ${color("common.white")};
    }
    .special-h1 {
        font-size: ${fontConfig.h1Special.size};
        line-height: ${fontConfig.h1Special.lineHeight};
        font-weight: ${fontConfig.weight.bold};
        font-family: ${fontConfig.secondaryFontFamily};
        text-transform: uppercase;
        margin-bottom: 20px;
    }
    .special-h2 {
        font-size: ${fontConfig.h2Special.size};
        line-height: ${fontConfig.h2Special.lineHeight};
        font-weight: ${fontConfig.weight.bold};
        font-family: ${fontConfig.secondaryFontFamily};
        text-transform: uppercase;
        margin-bottom: 18px;
    }
`;

export const tableStyles = css`
    table.vmi-table-style {
        border-collapse: collapse;
        width: 100%;
        thead {
            tr {
                background: ${color("common.ui7")};
            }
            th {
                background: ${color("common.ui7")};
                padding: 8px;
                border-bottom: 0;
                line-height: normal;
                white-space: nowrap;
                text-align: start;
                color: ${color("common.white")};
                &,
                span,
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                    font-weight: ${fontConfig.weight.bold};
                }
            }
        }
        tbody {
            tr {
                background: ${color("common.white")};
                &:nth-child(2n) {
                    background: ${color("common.ui2")};
                }
            }
            td {
                letter-spacing: 0;
                word-break: break-word;
                text-align: left;
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                min-width: 150px;
                &,
                span,
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                    font-weight: ${fontConfig.weight.regular};
                }
            }
        }
    }
`;

export const accordionStyles = css`
    .fr-accordion {
        min-width: 250px;
        ${styledScrollbar()}
        margin-bottom: 32px;

        .fr-accordion-header {
            padding: 8px 12px;
            background: ${color("primary")};
            display: flex;
            justify-content: space-between;
            align-items: center;
            &,
            .fr-accordion-title {
                color: ${color("common.white")};
                font-size: ${autoClamp(20, 20, 24)};
                font-weight: ${fontConfig.weight.bold};
                line-height: ${autoClamp(23, 23, 27)};
                font-family: ${fontConfig.secondaryFontFamily};
                flex-grow: 1;
            }

            .fr-accordion-toggle {
                display: inline-block;
                width: 24px;
                height: 21px;
                vertical-align: middle;
                margin-left: 10px;
                background-repeat: no-repeat;
                background-position: center;
                background-size: contain;
                transition: all 0.2s ease;
            }
        }
        .fr-accordion-body {
            padding: 12px;
            > p {
                font-size: ${fontConfig.regular.size}px;
                line-height: ${fontConfig.regularSingleLine.lineHeight};
                margin-bottom: 0;
            }
        }
        .fr-accordion-toggle {
            background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Cpath fill='%23FFF' d='M6.286.786A.776.776 0 0 0 5.5 0a.776.776 0 0 0-.786.786v3.928H.786A.776.776 0 0 0 0 5.5c0 .442.344.786.786.786h3.928v3.928c0 .442.344.786.786.786a.776.776 0 0 0 .786-.786V6.286h3.928A.776.776 0 0 0 11 5.5a.776.776 0 0 0-.786-.786H6.286V.786Z'/%3E%3C/svg%3E");
        }

        &[open] .fr-accordion-toggle {
            background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 2'%3E%3Cpath fill='%23FFF' d='M0 1c0-.563.344-1 .786-1h9.428c.442 0 .786.438.786 1 0 .563-.344 1-.786 1H.786C.344 2 0 1.562 0 1Z'/%3E%3C/svg%3E");
        }
    }
`;

const linkVariants = [
    { name: "primary", color: "primary", hoverColor: "common.ui15" },
    { name: "secondary", color: "secondary", hoverColor: "common.ui17" },
    { name: "tertiary", color: "common.ui19", hoverColor: "common.ui19" },
    { name: "quarternary", color: "common.ui15", hoverColor: "common.ui15" },
];

const generateLinkVariantStyles = () => {
    return css`
        ${linkVariants.map(
            ({ name, color, hoverColor }) => `
           a.${name} {
                &,span,svg{
                    color: ${getSystemColor(color)} !important;
                }
                &:hover {
                    &,span,svg{
                        color: ${getSystemColor(hoverColor)} !important;
                    }
                }
            }
        `,
        )}
    `;
};

export const linkTypoStyles = css`
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        & > ${StyledButton} > ${TypographyStyle} {
            font-size: inherit;
            line-height: inherit;
            font-weight: inherit;
            font-family: inherit;
        }
    }

    ${generateLinkVariantStyles()}
`;

export default {
    css: css`
        ${hideLoadingOverlay}
        ${xngLoadingOverlayUtilitiesStyles}
        .enlarged-img {
            z-index: 1;
            background: ${getSystemColor("common.white")};
        }
        body {
            // Fixes Adelle font rendering too thin on Chrome/macOS.
            -webkit-font-smoothing: antialiased;

            &:after {
                transition: all 0.2s linear;
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                z-index: ${getProp("theme.zIndex.shade")};
                width: 100%;
                height: 100%;
                pointer-events: none;
            }
            &.shade {
                &:after {
                    background-color: ${color("common.ui7", 0.7)};
                }
            }
            &.drawer-open ${StyledOverlayComponent} {
                z-index: ${getProp("theme.zIndex.header")};
            }
        }

        // This style to fix an issue with Google Places autocomplete
        .pac-container {
            z-index: 10000 !important;
        }

        .date-picker-wrapper {
            display: flex;
        }

        .hide-on-tablet {
            ${xScreen("lte", "md")} {
                display: none;
            }
        }

        .show-on-tablet {
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
        }

        ${specialClassesStyles}
        ${tableStyles}
        ${accordionStyles}
        ${linkTypoStyles}
        .fr-accordion-header {
            cursor: pointer;
        }

        ${menuBodyStyle}

        /* General Selector Styles */
        p + h1,
        p + h2,
        p + h3 {
            margin-top: 32px; // spacing between sections
        }
        ul + p,
        ol + p {
            margin-top: 24px; // spacing when p after ul/ol
        }
        h1:first-child,
        h2:first-child,
        h3:first-child {
            margin-top: 0;
        }
    `,
} as xBaseTheme["globalStyle"];
